/* eslint-disable react/no-danger */
import { CSS_SELECTOR, useCustomCss } from '../../contexts/CustomCss';
import { Fragment, FunctionalComponent, h } from 'preact';
import { ShowLess, ShowMore, Text } from './ShowMore.styles';
import { useEffect, useRef, useState } from 'preact/hooks';
import { SHOW_MORE_ID } from '../../constants';
import { UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { VIEW } from '../../enums';
import { memo } from 'preact/compat';
import { useDeviceState } from '../../hooks';
import { useUiContext } from '../../contexts/UiContext';

type Props = {
  enableHTMLTextFormatting: boolean;
  isShowMoreEnabled?: boolean;
  labelShowLess?: string;
  labelShowMore?: string;
  longDescription?: string;
  maxDescriptionHeight?: number;
  shortDescription?: string | null;
  withFading?: boolean;
};

const ShowMoreComponent: FunctionalComponent<Props> = memo(
  ({
    enableHTMLTextFormatting = false,
    isShowMoreEnabled = false,
    labelShowLess,
    labelShowMore,
    longDescription,
    maxDescriptionHeight,
    shortDescription,
  }) => {
    const { rules: descriptionRules } = useCustomCss(CSS_SELECTOR.MESSAGE);
    const { rules: linkRules } = useCustomCss(CSS_SELECTOR.LINKS);
    const { isMobileExtraSmall, isMobileSmall, isLandscape } = useDeviceState();
    const { uiVariant, view, setView } = useUiContext();
    const [shouldDisplayShortDescription, displayShortDescription] = useState(isShowMoreEnabled && isMobileSmall);
    const [shouldDisplayShowLessButton, displayShowLessButton] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      displayShortDescription(isShowMoreEnabled && (isMobileSmall || isMobileExtraSmall));
    }, [isLandscape, isMobileExtraSmall, isMobileSmall, isShowMoreEnabled, setView]);

    useEffect(() => {
      displayShowLessButton(
        isShowMoreEnabled && (isMobileSmall || isMobileExtraSmall) && !shouldDisplayShortDescription,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldDisplayShortDescription]);

    const shouldLimitHeight = uiVariant === UI_VARIANT.TCF && view === VIEW.FIRST_LAYER;

    return (
      <Fragment>
        {shouldDisplayShortDescription ? (
          <Fragment>
            {shortDescription && shortDescription !== '' && (
              <Text id={SHOW_MORE_ID} maxHeight={maxDescriptionHeight} shouldLimitHeight={shouldLimitHeight}>
                {enableHTMLTextFormatting ? (
                  <div dangerouslySetInnerHTML={{ __html: shortDescription || '' }} />
                ) : (
                  shortDescription
                )}
              </Text>
            )}
            <ShowMore
              onClick={(): void => displayShortDescription(!shouldDisplayShortDescription)}
              data-testid="uc-show-more"
              style={{ ...linkRules }}
            >
              {`${labelShowMore}...`}
            </ShowMore>
          </Fragment>
        ) : (
          <Fragment>
            {longDescription && longDescription !== '' && (
              <Fragment>
                <Text
                  id={SHOW_MORE_ID}
                  maxHeight={maxDescriptionHeight}
                  ref={textRef}
                  shouldLimitHeight={shouldLimitHeight}
                  style={{ ...descriptionRules }}
                >
                  {enableHTMLTextFormatting ? (
                    <div dangerouslySetInnerHTML={{ __html: longDescription || '' }} />
                  ) : (
                    longDescription
                  )}
                </Text>
                {shouldDisplayShowLessButton && (
                  <ShowLess
                    onClick={(): void => displayShortDescription(!shouldDisplayShortDescription)}
                    data-testid="uc-show-less"
                    style={{ ...linkRules }}
                  >
                    {`${labelShowLess}...`}
                  </ShowLess>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Fragment>
    );
  },
);

export default ShowMoreComponent;
